/**
 * Test page for Stripe Custom Checkout
 * Access at: /test-mux-country
 * 
 * Tests the complete checkout flow with real Stripe integration:
 * - Country-specific pricing
 * - Email collection
 * - Stripe Elements payment form
 * - Payment Intent creation
 * - Webhook handling
 * - Database tracking
 */

import { BRAND_PERSON_NAME, BRAND_SITE_HOST, BRAND_SOURCE_KEY } from "@/lib/brandConfig";
import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import MuxVideoPlayerWithCTA from "@/components/MuxVideoPlayerWithCTA";
import { getAllPacks } from "@/config/stripe";
import { usePackConfig } from "@/lib/usePackConfig";
import type { VideoCTAService } from "@/config/videoCTAConfig";

// Countries to test
const TEST_COUNTRIES = [
  { code: "US", name: "United States", flag: "🇺🇸" },
  { code: "AR", name: "Argentina", flag: "🇦🇷" },
  { code: "MX", name: "Mexico", flag: "🇲🇽" },
  { code: "ES", name: "Spain", flag: "🇪🇸" },
  { code: "CO", name: "Colombia", flag: "🇨🇴" },
  { code: "CL", name: "Chile", flag: "🇨🇱" },
  { code: "PE", name: "Peru", flag: "🇵🇪" },
  { code: "BR", name: "Brazil", flag: "🇧🇷" },
  { code: "UY", name: "Uruguay", flag: "🇺🇾" },
  { code: "EC", name: "Ecuador", flag: "🇪🇨" },
  { code: "BO", name: "Bolivia", flag: "🇧🇴" },
  { code: "PY", name: "Paraguay", flag: "🇵🇾" },
  { code: "VE", name: "Venezuela", flag: "🇻🇪" },
  { code: "GB", name: "United Kingdom", flag: "🇬🇧" },
  { code: "DE", name: "Germany", flag: "🇩🇪" },
  { code: "FR", name: "France", flag: "🇫🇷" },
  { code: "IT", name: "Italy", flag: "🇮🇹" },
  { code: "CA", name: "Canada", flag: "🇨🇦" },
];

// Available products (only channel_invite is public)
const PRODUCTS = getAllPacks().filter(pack => pack.id === "channel_invite");

export default function TestMuxCountry() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedCountry, setSelectedCountry] = useState<string>(
    searchParams.get("country") || "US"
  );
  const [selectedProduct, setSelectedProduct] = useState<string>(
    searchParams.get("product") || "channel_invite"
  );

  const handleCountryChange = (countryCode: string) => {
    setSelectedCountry(countryCode);
    setSearchParams({ country: countryCode, product: selectedProduct });
  };

  const handleProductChange = (productId: string) => {
    setSelectedProduct(productId);
    setSearchParams({ country: selectedCountry, product: productId });
  };

  // Use usePackConfig (same as production - API + static fallback)
  const { getPackPricing } = usePackConfig();
  const pricing = getPackPricing(selectedProduct, selectedCountry);
  const defaultPricing = getPackPricing(selectedProduct);

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 via-purple-900 to-gray-900">
      {/* Header */}
      <div className="bg-black/50 backdrop-blur-sm border-b border-purple-500/20 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <h1 className="text-2xl font-bold text-white mb-2">
            🧪 Stripe Custom Checkout Test
          </h1>
          <p className="text-gray-400 text-sm">
            Test the complete checkout flow with real Stripe integration
          </p>
          <div className="mt-2 flex flex-wrap gap-3 text-sm">
            <a href="/test-mux-video" className="text-purple-400 hover:text-purple-300 transition-colors">
              🎬 Video fullscreen (modo prod)
            </a>
            <a href="/checkout-channel-invite" className="text-purple-400 hover:text-purple-300 transition-colors">
              📄 Checkout directo
            </a>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Country Selector */}
          <div className="bg-black/30 backdrop-blur-sm rounded-lg border border-purple-500/20 p-6">
            <h2 className="text-lg font-semibold text-white mb-4">
              🌍 Select Country
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-96 overflow-y-auto">
              {TEST_COUNTRIES.map((country) => (
                <button
                  key={country.code}
                  onClick={() => handleCountryChange(country.code)}
                  className={`
                    px-3 py-2 rounded-lg text-sm font-medium ui-transition
                    ${
                      selectedCountry === country.code
                        ? "bg-purple-600 text-white shadow-lg shadow-purple-500/50"
                        : "bg-gray-800/50 text-gray-300 hover:bg-gray-700/50"
                    }
                  `}
                >
                  <span className="mr-2">{country.flag}</span>
                  {country.code}
                </button>
              ))}
            </div>
            <div className="mt-4 pt-4 border-t border-purple-500/20">
              <p className="text-sm text-gray-400">
                Selected: <span className="text-white font-semibold">{selectedCountry}</span>
              </p>
            </div>
          </div>

          {/* Product Selector */}
          <div className="bg-black/30 backdrop-blur-sm rounded-lg border border-purple-500/20 p-6">
            <h2 className="text-lg font-semibold text-white mb-4">
              📦 Select Product
            </h2>
            <div className="space-y-2">
              {PRODUCTS.map((product) => (
                <button
                  key={product.id}
                  onClick={() => handleProductChange(product.id)}
                  className={`
                    w-full px-4 py-3 rounded-lg text-left ui-transition
                    ${
                      selectedProduct === product.id
                        ? "bg-purple-600 text-white shadow-lg shadow-purple-500/50"
                        : "bg-gray-800/50 text-gray-300 hover:bg-gray-700/50"
                    }
                  `}
                >
                  <div className="font-medium">{product.name}</div>
                  <div className="text-sm opacity-75">{product.description}</div>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Pricing Info */}
        <div className="bg-black/30 backdrop-blur-sm rounded-lg border border-purple-500/20 p-6 mb-6">
          <h2 className="text-lg font-semibold text-white mb-4">
            💰 Pricing Information
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <p className="text-sm text-gray-400 mb-1">Default Price</p>
              <p className="text-2xl font-bold text-white">
                ${(defaultPricing!.amount / 100).toFixed(2)} {defaultPricing!.currency.toUpperCase()}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-400 mb-1">
                Price for {selectedCountry}
              </p>
              <p className="text-2xl font-bold text-purple-400">
                ${(pricing!.amount / 100).toFixed(2)} {pricing!.currency.toUpperCase()}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-400 mb-1">Discount</p>
              <p className="text-2xl font-bold text-green-400">
                {pricing!.amount === defaultPricing!.amount
                  ? "No discount"
                  : `${Math.round(
                      ((defaultPricing!.amount - pricing!.amount) /
                        defaultPricing!.amount) *
                        100
                    )}% OFF`}
              </p>
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-purple-500/20">
            <p className="text-sm text-gray-400">
              <span className="font-semibold text-white">Product:</span> {pricing!.name}
            </p>
            <p className="text-sm text-gray-400 mt-1">
              <span className="font-semibold text-white">Description:</span> {pricing!.description}
            </p>
          </div>
        </div>

        {/* Test Instructions */}
        <div className="bg-yellow-500/10 backdrop-blur-sm rounded-lg border border-yellow-500/30 p-6 mb-6">
          <h2 className="text-lg font-semibold text-yellow-400 mb-3">
            ⚠️ Test Mode Instructions
          </h2>
          <div className="space-y-2 text-sm text-gray-300">
            <p>
              <strong>Test Card:</strong> 4242 4242 4242 4242
            </p>
            <p>
              <strong>Expiry:</strong> Any future date (e.g., 12/34)
            </p>
            <p>
              <strong>CVC:</strong> Any 3 digits (e.g., 123)
            </p>
            <p>
              <strong>ZIP:</strong> Any 5 digits (e.g., 12345)
            </p>
            <p className="pt-2 border-t border-yellow-500/20">
              <strong>What happens:</strong>
            </p>
            <ul className="list-disc list-inside space-y-1 ml-4">
              <li>Payment Intent created via /api/create-payment-intent</li>
              <li>Stripe Elements (PaymentElement) with custom styling</li>
              <li>Test payment processed (no real charge)</li>
              <li>Webhook receives payment_intent.succeeded event</li>
              <li>Purchase saved to PostgreSQL database</li>
              <li>Redirect to /thank-you or /gracias (by language)</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Video Player with Checkout */}
      <div className="max-w-4xl mx-auto px-4 pb-12">
        <div className="bg-black/30 backdrop-blur-sm rounded-lg border border-purple-500/20 p-6">
          <h2 className="text-lg font-semibold text-white mb-4">
            🎬 Video Player with Custom Checkout
          </h2>
          <MuxVideoPlayerWithCTA
            service={selectedProduct as VideoCTAService}
            countryCode={selectedCountry}
            displayMode="reel"
            loop
          />
        </div>
      </div>

      {/* Footer Info */}
      <div className="max-w-7xl mx-auto px-4 pb-12">
        <div className="bg-black/30 backdrop-blur-sm rounded-lg border border-purple-500/20 p-6">
          <h2 className="text-lg font-semibold text-white mb-3">
            📊 What Gets Tracked
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-300">
            <div>
              <h3 className="font-semibold text-white mb-2">Payment Intent Metadata:</h3>
              <ul className="list-disc list-inside space-y-1">
                <li>source: "{BRAND_SOURCE_KEY}"</li>
                <li>site: "{BRAND_SITE_HOST}"</li>
                <li>brand: {BRAND_PERSON_NAME}</li>
                <li>pack_id: "{selectedProduct}"</li>
                <li>country: "{selectedCountry}"</li>
                <li>buyer_email: (from form)</li>
                <li>checkout_type: "custom_elements"</li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold text-white mb-2">PostgreSQL Database:</h3>
              <ul className="list-disc list-inside space-y-1">
                <li>Payment Intent ID</li>
                <li>Amount and currency</li>
                <li>Buyer email</li>
                <li>Country and geolocation</li>
                <li>Status: succeeded/failed</li>
                <li>Timestamps</li>
                <li>Instagram/Telegram access tracking</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
